/**
 * Spatial Campus Navigation Types
 *
 * Types and interfaces for the 3D campus navigation system that replaces
 * tab-based navigation with an immersive spatial experience.
 */

/**
 * Campus locations representing distinct physical areas of the company
 */
export enum CampusLocation {
  CAMPUS_OVERVIEW = 'campus',
  HEADQUARTERS = 'hq',
  DESIGN_STUDIO = 'design-studio',
  RD_LAB = 'rd-lab',
  MANUFACTURING = 'factory',
  SALES_OFFICE = 'sales-office',
  EXECUTIVE_OFFICE = 'executive',
  COMPANY_ARCHIVES = 'archives',
}

/**
 * Constant object for type-safe location references
 */
export const CAMPUS_LOCATIONS = {
  CAMPUS_OVERVIEW: CampusLocation.CAMPUS_OVERVIEW,
  HEADQUARTERS: CampusLocation.HEADQUARTERS,
  DESIGN_STUDIO: CampusLocation.DESIGN_STUDIO,
  RD_LAB: CampusLocation.RD_LAB,
  MANUFACTURING: CampusLocation.MANUFACTURING,
  SALES_OFFICE: CampusLocation.SALES_OFFICE,
  EXECUTIVE_OFFICE: CampusLocation.EXECUTIVE_OFFICE,
  COMPANY_ARCHIVES: CampusLocation.COMPANY_ARCHIVES,
} as const;

/**
 * 3D position in campus space
 */
export interface CampusPosition {
  x: number;
  y: number;
  z: number;
}

/**
 * Configuration for a campus location
 */
export interface LocationConfig {
  id: CampusLocation;
  name: string;
  description: string;
  buildingName: string;
  position: CampusPosition;
  rotation: number;
  color: string;
  lockedColor: string;
  adjacentLocations: CampusLocation[];
  unlockRequirements?: UnlockRequirement[];
}

/**
 * Requirements to unlock a location
 */
export interface UnlockRequirement {
  type: 'cash' | 'products' | 'employees' | 'reputation' | 'research' | 'productsInDevelopment' | 'productsReleased';
  value: number;
  description: string;
}

/**
 * Navigation history entry for breadcrumb trail
 */
export interface NavigationHistoryItem {
  location: CampusLocation;
  timestamp: number;
}

/**
 * Transition animation types
 */
export type TransitionType = 'fade' | 'slide' | 'zoom' | 'none';

/**
 * Direction for slide transitions
 */
export type TransitionDirection = 'left' | 'right' | 'up' | 'down';

/**
 * Configuration for location transitions
 */
export interface TransitionConfig {
  type: TransitionType;
  duration: number;
  easing: string;
  direction?: TransitionDirection;
}

/**
 * User preferences for transitions
 */
export interface TransitionPreferences {
  enabled: boolean;
  reducedMotion: boolean;
  defaultType: TransitionType;
  defaultDuration: number;
}

/**
 * Stats displayed on building hover/selection
 */
export interface BuildingStats {
  employees?: number;
  revenue?: number;
  productsInProgress?: number;
  researchProjects?: number;
  capacity?: number;
  utilization?: number;
}

/**
 * State for the selected building panel
 */
export interface SelectedBuildingState {
  location: CampusLocation | null;
  isHovered: boolean;
  stats: BuildingStats | null;
}

/**
 * Props for 3D building components
 */
export interface Building3DProps {
  location: CampusLocation;
  config: LocationConfig;
  isUnlocked: boolean;
  isSelected: boolean;
  isHovered: boolean;
  onClick: (location: CampusLocation) => void;
  onHover: (location: CampusLocation | null) => void;
}

/**
 * Props for the campus scene
 */
export interface CampusSceneProps {
  buildings: LocationConfig[];
  unlockedLocations: CampusLocation[];
  selectedLocation: CampusLocation | null;
  onBuildingClick: (location: CampusLocation) => void;
  onBuildingHover: (location: CampusLocation | null) => void;
}

/**
 * Props for the location wrapper component
 */
export interface LocationWrapperProps {
  location: CampusLocation;
  title: string;
  backgroundColor?: string;
  children: React.ReactNode;
}

/**
 * Mapping from old dashboard tabs to campus locations
 */
export const TAB_TO_LOCATION_MAP: Record<string, CampusLocation> = {
  Headquarters: CampusLocation.HEADQUARTERS,
  Products: CampusLocation.DESIGN_STUDIO,
  Operations: CampusLocation.MANUFACTURING,
  Research: CampusLocation.RD_LAB,
  Marketing: CampusLocation.SALES_OFFICE,
  Finance: CampusLocation.EXECUTIVE_OFFICE,
  Archives: CampusLocation.COMPANY_ARCHIVES,
  Company: CampusLocation.HEADQUARTERS, // Company is a sub-view of HQ
};

/**
 * Reverse mapping from locations to dashboard tabs
 */
export const LOCATION_TO_TAB_MAP: Record<CampusLocation, string> = {
  [CampusLocation.CAMPUS_OVERVIEW]: 'Headquarters',
  [CampusLocation.HEADQUARTERS]: 'Headquarters',
  [CampusLocation.DESIGN_STUDIO]: 'Products',
  [CampusLocation.MANUFACTURING]: 'Operations',
  [CampusLocation.RD_LAB]: 'Research',
  [CampusLocation.SALES_OFFICE]: 'Marketing',
  [CampusLocation.EXECUTIVE_OFFICE]: 'Finance',
  [CampusLocation.COMPANY_ARCHIVES]: 'Archives',
};
